import React, { useEffect, useRef, useCallback } from "react";
import { useAdminCheck } from "@/hooks/useAdminCheck";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Shield, AlertTriangle, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { clearNativePushToken } from "@/lib/nativePush";
import { resetAnalyticsIdentity } from "@/utils/analyticsIdentity";

interface AdminRouteGuardProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
  /** If true, allows both admin and super_admin roles. Default checks for super_admin only. */
  allowAdmin?: boolean;
}

/**
 * Server-side verified admin route guard.
 * Uses the has_role() database function to prevent client-side bypass.
 */
const IDLE_TIMEOUT_MS = 15 * 60 * 1000;

const AdminRouteGuard: React.FC<AdminRouteGuardProps> = ({
  children,
  fallback,
  allowAdmin = false
}) => {
  const { isAdmin, isSuperAdmin, isLoading, error } = useAdminCheck();
  const idleTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const resetTimer = useCallback(() => {
    if (idleTimer.current) clearTimeout(idleTimer.current);
    idleTimer.current = setTimeout(async () => {
      // Native only, no-op on web: evict this device's FCM token before the
      // idle session is cleared (needs a live session to resolve auth.getUser).
      await clearNativePushToken();
      // Real logout (idle timeout), not a session swap — drop the analytics
      // identity too, or this admin's id stays attached to whoever uses the
      // device next. This path does not go through authService.signOut.
      resetAnalyticsIdentity();
      await supabase.auth.signOut();
    }, IDLE_TIMEOUT_MS);
  }, []);

  useEffect(() => {
    if (!isAdmin && !isSuperAdmin) return;
    const events = ['mousemove', 'keydown', 'touchstart', 'click'] as const;
    events.forEach(e => window.addEventListener(e, resetTimer, { passive: true }));
    resetTimer();
    return () => {
      events.forEach(e => window.removeEventListener(e, resetTimer));
      if (idleTimer.current) clearTimeout(idleTimer.current);
    };
  }, [isAdmin, isSuperAdmin, resetTimer]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">Verifying admin access...</p>
        </div>
      </div>
    );
  }

  if (error) {
    console.error('AdminRouteGuard: Verification error:', error);
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Alert className="max-w-md" variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            Unable to verify admin access. Please try refreshing the page or contact support if the issue persists.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  // Check access: allowAdmin=true accepts admin OR super_admin, otherwise super_admin only
  const hasAccess = allowAdmin ? isAdmin : isSuperAdmin;

  if (!hasAccess) {
    if (fallback) {
      return <>{fallback}</>;
    }

    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Alert className="max-w-md">
          <Shield className="h-4 w-4" />
          <AlertDescription>
            You don't have permission to access this admin area. This access check is verified server-side and cannot be bypassed. Please contact your system administrator if you believe this is an error.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return <>{children}</>;
};

export default AdminRouteGuard;
